import { getKey, ProviderError } from './shared';
import { t } from '../modules/i18n';

async function callGoogleOnce(model, prompt, signal, key, opts) {
  const maxOut = (opts && opts.maxOutTokens) || 8192;
  // Build the parts array. Gemini accepts inlineData parts for binary
  // (images + PDFs both supported natively). Order: visuals/docs first
  // so the model has them as context before reading the instruction.
  const parts = [];
  const attParts = (opts && opts.parts) || [];
  for (const a of attParts) {
    if ((a.kind === 'image' || a.kind === 'pdf') && a.base64) {
      parts.push({
        inlineData: { mimeType: a.mime || (a.kind === 'pdf' ? 'application/pdf' : 'image/png'), data: a.base64 },
      });
    }
  }
  parts.push({ text: prompt });
  const url = `https://generativelanguage.googleapis.com/v1beta/models/${encodeURIComponent(model)}:generateContent?key=${encodeURIComponent(key)}`;
  const r = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      contents: [{ parts }],
      generationConfig: {
        maxOutputTokens: maxOut,
        temperature: 0.7,
      },
    }),
    signal,
  });
  if (!r.ok) {
    const err = await r.text();
    throw new ProviderError("Google", r.status, err);
  }
  const data = await r.json();

  // 1. API-level error inside a 200 response
  if (data.error) {
    throw new ProviderError("Google", data.error.code || 200, JSON.stringify(data));
  }

  // 2. No candidates returned at all (sometimes happens on first cold-start call)
  const cand = data.candidates && data.candidates[0];
  if (!cand) {
    return { text: "", finishReason: "NO_CANDIDATE", raw: data };
  }

  // 3. Safety / recitation / blocked
  const fr = cand.finishReason || "";
  if (fr === "SAFETY" || fr === "RECITATION" || fr === "BLOCKLIST") {
    throw new ProviderError("Google", 200,
      JSON.stringify({ error: { message: `Content blocked by Gemini (${fr}). Try rephrasing.` } }));
  }

  // 4. Extract text from response parts (different from the request `parts`
  // we built above — rename to avoid collision).
  const respParts = cand.content?.parts || [];
  const text = respParts.map(p => p.text || "").join("").trim();
  const um = data.usageMetadata || {};
  return {
    text,
    finishReason: fr,
    raw: data,
    inTokens:  um.promptTokenCount      || 0,
    outTokens: um.candidatesTokenCount  || 0,
  };
}

export async function callGoogle(model, prompt, signal, opts) {
  const key = getKey("google");
  if (!key) throw new Error(t("err.nokey", { provider: "Google" }));

  // Gemini 2.5 (especially Pro) often returns an empty body on the first call
  // due to thinking-mode cold start. Auto-retry once with a small backoff.
  // We DON'T retry on MAX_TOKENS — that means the model ran out of output
  // budget; retrying with the same cap won't help. Surface truncated=true.
  let result = await callGoogleOnce(model, prompt, signal, key, opts);
  if (!result.text && (result.finishReason === "NO_CANDIDATE" || result.finishReason === "")) {
    await new Promise(r => setTimeout(r, 900));
    result = await callGoogleOnce(model, prompt, signal, key, opts);
  }

  if (!result.text) {
    // Surface the raw payload so the user can see what Gemini actually returned
    const detail = JSON.stringify(result.raw).slice(0, 400);
    throw new ProviderError("Google", 200,
      JSON.stringify({ error: { message: `Empty response from Gemini${result.finishReason ? " (finishReason: " + result.finishReason + ")" : ""}. Try Re-run or use a simpler prompt.\n\nRaw: ${detail}` } }));
  }
  return {
    text: result.text,
    inTokens: result.inTokens,
    outTokens: result.outTokens,
    finishReason: result.finishReason,
    truncated: result.finishReason === "MAX_TOKENS",
  };
}
